/**
 * The magic-link landing page — Step 10. Redemption is a POST from here, never
 * the GET itself: mail security scanners prefetch every link in every email,
 * and a GET that consumed the token would burn each link before the human saw
 * it. Bare fetch on purpose: portal auth domain.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../../lib/urlState';
import { useAuth } from '../../lib/AuthContext';

export function PortalRedeemPage() {
  const [tokenParam] = useUrlState('token', null);
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const fired = useRef(false); // StrictMode double-mount must not double-redeem

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    const token = tokenParam ?? '';
    fetch('/auth/portal/redeem', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        const body = await res.json().catch(() => null);
        if (!res.ok) {
          setError(body?.error ?? 'That link did not work.');
          return;
        }
        await refresh();
        navigate(body?.redirectTo ?? '/portal', { replace: true });
      })
      .catch(() => setError('Could not reach the server — try the link again.'));
  }, [tokenParam, navigate, refresh]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm rounded-lg border bg-card p-6 text-center">
        {error ? (
          <>
            <p className="text-sm font-medium text-destructive">{error}</p>
            <a className="mt-3 inline-block text-sm text-brand hover:underline" href="/portal/login">
              Request a fresh link
            </a>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Signing you in…</p>
        )}
      </div>
    </div>
  );
}
